import React, { useState, useEffect } from 'react'
import { 
  Box, 
  Typography, 
  Paper, 
  TextField, 
  Button, 
  Alert, 
  Link as MuiLink,
  CircularProgress,
  InputAdornment,
  IconButton
} from '@mui/material'
import { 
  Lock, 
  Visibility, 
  VisibilityOff,
  CheckCircle 
} from '@mui/icons-material'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Auth } from 'aws-amplify'
import { Layout } from '@/components/Layout'

interface ResetPasswordFormData {
  code: string
  newPassword: string
  confirmPassword: string
}

export default function ResetPassword() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const email = searchParams.get('email')
  const code = searchParams.get('code')
  
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue
  } = useForm<ResetPasswordFormData>({ mode: 'onChange' })

  const newPassword = watch('newPassword')

  useEffect(() => {
    if (code) {
      setValue('code', code)
    }
  }, [code, setValue])

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!email) {
      setError('Invalid reset link. Please request a new password reset.')
      return
    }

    setIsLoading(true)
    setError(null)
    setSuccess(null)

    try {
      await Auth.forgotPasswordSubmit(email, data.code, data.newPassword)
      setSuccess('Password reset successfully! You can now sign in with your new password.')
      
      // Redirect to login after successful reset
      setTimeout(() => {
        navigate('/auth/login')
      }, 2000)
    } catch (err: any) {
      console.error('Reset password error:', err)
      
      switch (err.code) {
        case 'CodeMismatchException':
          setError('Invalid verification code. Please check and try again.')
          break
        case 'ExpiredCodeException':
          setError('Verification code has expired. Please request a new password reset.')
          break
        case 'InvalidPasswordException':
          setError('Password does not meet requirements. Please ensure it has at least 8 characters with uppercase, lowercase, numbers, and special characters.')
          break
        case 'UserNotFoundException':
          setError('User not found. Please check the reset link.')
          break
        default:
          setError('An error occurred during password reset. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const resendCode = async () => {
    if (!email) {
      setError('Invalid email. Please request a new password reset.')
      return
    }

    try {
      await Auth.forgotPassword(email)
      setSuccess('New verification code sent to your email.')
      setError(null)
    } catch (err: any) {
      setError('Failed to resend verification code. Please try again.')
    }
  }

  if (!email) {
    return (
      <Layout showNavigation={false}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '70vh',
            px: 2,
          }}
        >
          <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%' }}>
            <Typography variant="h5" component="h1" gutterBottom align="center">
              Invalid Reset Link
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
              This password reset link is invalid or has expired.
            </Typography>
            
            <Box sx={{ textAlign: 'center' }}>
              <Button variant="contained" fullWidth component={Link} to="/auth/forgot-password">
                Request New Reset Link
              </Button>
            </Box>
          </Paper>
        </Box>
      </Layout>
    )
  }

  return (
    <Layout showNavigation={false}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '70vh',
          px: 2,
        }}
      >
        <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%' }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <CheckCircle color="primary" sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="h5" component="h1" gutterBottom>
              Reset Your Password
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enter your new password for {email}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <TextField
              {...register('code', {
                required: 'Verification code is required',
                pattern: {
                  value: /^\d{6}$/,
                  message: 'Please enter a valid 6-digit code'
                }
              })}
              fullWidth
              label="Verification Code"
              placeholder="Enter 6-digit code"
              margin="normal"
              error={!!errors.code}
              helperText={errors.code?.message || 'Check your email for the verification code'}
              disabled={isLoading}
            />

            <TextField
              {...register('newPassword', {
                required: 'New password is required',
                minLength: {
                  value: 8,
                  message: 'Password must be at least 8 characters'
                },
                pattern: {
                  value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                  message: 'Password must contain uppercase, lowercase, number, and special character'
                }
              })}
              fullWidth
              label="New Password"
              type={showNewPassword ? 'text' : 'password'}
              autoComplete="new-password"
              margin="normal"
              error={!!errors.newPassword}
              helperText={errors.newPassword?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle new password visibility"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              disabled={isLoading}
            />

            <TextField
              {...register('confirmPassword', {
                required: 'Please confirm your new password',
                validate: (value) => value === newPassword || 'Passwords do not match'
              })}
              fullWidth
              label="Confirm New Password"
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="new-password"
              margin="normal"
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              disabled={isLoading}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Reset Password'
              )}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Didn't receive the code?{' '}
                <MuiLink
                  component="button"
                  type="button"
                  onClick={resendCode}
                  sx={{ textDecoration: 'none' }}
                >
                  Resend
                </MuiLink>
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <MuiLink component={Link} to="/auth/login" variant="body2" sx={{ textDecoration: 'none' }}>
                ← Back to Sign In
              </MuiLink>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Layout>
  )
}